import { useIntl } from 'react-intl';
import { z } from 'zod';

import {
  useChannelRefinementValidators,
  validateEndDateIsAfterStartDate,
  validateMaxDuration,
} from './channel-refinements';
import { usePoolDetailsSchema } from './pool-schema';

/**
 * Hook to create the validation schema for channel configuration
 * including budget allocation, dates, and per-opening budgets.
 *
 * Pool channels (`slug === 'pool'`) store their campaign-specific config in
 * `poolDetails` and use `isFree: true` so budget validation is skipped. The
 * end-date required check is also relaxed for pool entries that have
 * `poolDetails.isNeverEnding === true`.
 */
export function useChannelSchema() {
  const intl = useIntl();
  const poolDetailsSchema = usePoolDetailsSchema();

  const messages = {
    budgetRequired: intl.formatMessage({
      defaultMessage: 'Budget is required',
      description: 'Budget input required error message',
    }),
    endDateRequired: intl.formatMessage({
      defaultMessage: 'End date is required',
      description: 'End date input required error message',
    }),
    endDateMustBeLaterThanStartDate: intl.formatMessage({
      defaultMessage: 'End date must be later than start date',
      description: 'End date input validation error message',
    }),
    endDateExceedsMaxDuration: intl.formatMessage({
      defaultMessage: 'End date exceeds the maximum campaign duration',
      description:
        'End date input validation error when the campaign is longer than the channel maximum duration',
    }),
  };

  const {
    validatePerFunnelMinimumBudgets,
    validateOpeningBudgetsTotalMatchesChannelBudget,
    validateEvenAllocationMinimumBudget,
  } = useChannelRefinementValidators();

  return z.record(
    z.string(),
    z
      .object({
        startsAt: z.string(),
        // endAt is nullable — the refine below enforces "required unless pool+isNeverEnding"
        endAt: z.string().nullable(),
        budgetType: z
          .enum(['daily', 'weekly', 'monthly', 'lifetime'])
          .nullable(),
        openingBudgets: z.record(
          z.string(),
          z
            .number({
              message: messages.budgetRequired,
            })
            .nullable(),
        ),
        budgetAllocation: z.enum(['evenly', 'per-funnel']),
        channelBudget: z
          .number({
            message: messages.budgetRequired,
          })
          .nullable(),
        isExpanded: z.boolean(),
        endAtType: z.enum(['fixed', 'target']).nullable(),
        slug: z.string(),
        isFree: z.boolean(),
        poolDetails: poolDetailsSchema.nullable().optional(),
      })
      .superRefine((data, ctx) => {
        // Free channels (including Pool) skip budget validation.
        if (!data.isFree) {
          let hasMissingBudget = false;

          if ((data.channelBudget ?? 0) < 1) {
            ctx.addIssue({
              code: 'custom',
              message: messages.budgetRequired,
              path: ['channelBudget'],
            });
            hasMissingBudget = true;
          }

          Object.entries(data.openingBudgets).forEach(([openingId, budget]) => {
            if ((budget ?? 0) < 1) {
              ctx.addIssue({
                code: 'custom',
                message: messages.budgetRequired,
                path: ['openingBudgets', openingId],
              });
              hasMissingBudget = true;
            }
          });

          if (hasMissingBudget) return;

          validatePerFunnelMinimumBudgets(data, ctx);
          validateOpeningBudgetsTotalMatchesChannelBudget(data, ctx);
          validateEvenAllocationMinimumBudget(data, ctx);
        }

        // endAt is required for all channels EXCEPT pool with isNeverEnding.
        const isPoolNeverEnding = data.poolDetails?.isNeverEnding === true;
        if (!isPoolNeverEnding && (data.endAt === null || data.endAt === '')) {
          ctx.addIssue({
            code: 'custom',
            message: messages.endDateRequired,
            path: ['endAt'],
          });
        }
      })
      // Validate that the end date is after the start date (skip when endAt is null)
      .refine(
        data => {
          if (!data.endAt) return true;
          return validateEndDateIsAfterStartDate(data);
        },
        {
          message: messages.endDateMustBeLaterThanStartDate,
          path: ['endAt'],
        },
      )
      // Validate that the campaign does not exceed the channel's maximum duration
      .refine(data => validateMaxDuration(data), {
        message: messages.endDateExceedsMaxDuration,
        path: ['endAt'],
      }),
  );
}
